import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageTransition from "@/components/PageTransition";
import CTABlock from "@/components/CTABlock";
import { SERVICES } from "@/data/site";

export default function Services() {
  return (
    <PageTransition>
      <section className="pt-40 md:pt-56 pb-16 md:pb-24 relative overflow-hidden" data-testid="services-page">
        <div className="absolute inset-0 halftone-bg opacity-30 halftone-fade pointer-events-none" aria-hidden />
        <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-mono text-[11px] tracking-[0.32em] uppercase text-[color:var(--text-2)]"
          >
            — Services
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-display mt-6 text-[15vw] md:text-[128px] leading-[0.92] tracking-[-0.03em]"
          >
            What we <em className="italic text-[color:var(--accent)]">print.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25 }}
            className="mt-8 max-w-[680px] text-[color:var(--text-2)] text-[17px] leading-[1.65]"
          >
            Six core categories, one shared standard of craft. Every service is quoted individually — tell us what you need and we'll design a print plan that fits.
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-white" data-testid="services-detail-list">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="space-y-24 md:space-y-40">
            {SERVICES.map((s, i) => (
              <motion.article
                key={s.slug}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
                data-testid={`service-detail-${s.slug}`}
              >
                <div className={`md:col-span-7 relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[16/11] group ${i % 2 ? "md:order-2" : ""}`}>
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.05]"
                  />
                  <div className="absolute inset-0 halftone-bg opacity-[0.08] mix-blend-multiply" />
                </div>
                <div className={`md:col-span-5 ${i % 2 ? "md:order-1 md:pr-10" : "md:pl-10"}`}>
                  <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--text-2)] flex items-center gap-3">
                    <span className="text-[color:var(--accent)]">{s.number}</span> · {s.tag}
                  </div>
                  <h2 className="font-display text-5xl md:text-[64px] leading-[1] tracking-tight mt-6">
                    {s.title}
                  </h2>
                  <p className="mt-6 text-[16px] leading-[1.7] text-[color:var(--text-2)]">{s.blurb}</p>
                  <ul className="mt-6 space-y-2 text-[14.5px] text-[color:var(--text)]">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[color:var(--accent)]" />{f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/quote"
                    className="btn-accent mt-8 inline-flex items-center gap-2 rounded-full pl-6 pr-5 py-3 text-[12px] tracking-[0.16em] uppercase font-medium"
                    data-testid={`service-detail-cta-${s.slug}`}
                  >
                    Quote this service <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      <CTABlock />
    </PageTransition>
  );
}
